import { reactive } from '@rehaje/reactive'
import { Path, uid, values } from '@rehaje/shared'

import {
  getChildrenInNArray,
  getElementInNArray,
  getElementsInNArray
} from '../lambdas/nArray'
import { addElementEffect } from '../queues'
import { LifeCycle } from './LifeCycle'
import { ViewScope } from './ViewScope'

import type { Store } from '../stores/Store'
import type { InternalNode } from '../types'
import type { TypedNode } from './TypedNode'
import type { ViewModel } from './ViewModel'
import type { Filter, PathLikes } from '@rehaje/shared'

/**
 * 视图节点模型，主要面向 UI 型 DSL 的视图节点管理
 */
export class ViewElement<
  Node extends TypedNode = TypedNode,
  Model extends ViewModel = ViewModel
>
  extends LifeCycle
  implements InternalNode<ViewElement>
{
  override __r_debug_id = `viewElement:${uid()}`

  @reactive('ref', { observe: false })
  model?: Model

  node: Node
  scope: ViewScope

  get __r_address_graph(): Record<string, ViewElement> {
    if (this.root !== this) return this.root.__r_address_graph
    const graph: Record<string, ViewElement> = { '/': this }
    for (const element of this.descendants) {
      graph[element.address.toString()] = element
    }
    return graph
  }

  get __r_address_source() {
    return this.address.source
  }

  /**
   * 路径地址
   */
  get address(): Path {
    const indexes = this.indexes
    const index = this.index
    const parent = this.parent
    if (parent) {
      if (indexes.length > parent.indexes.length) {
        return parent.address.join([this.node.id, index])
      }
      return parent.address.join(this.node.id)
    }
    return new Path()
  }

  /**
   * 子节点集合
   */
  get children(): ViewElement[] {
    const elements: ViewElement[] = []
    this.node.__r_children.forEach((child) =>
      elements.push(
        ...getChildrenInNArray(child.__r_elements_graph, this.node.__r_indexes)
      )
    )
    return elements
  }

  /**
   * 子孙节点集合
   */
  get descendants(): ViewElement[] {
    const elements: ViewElement[] = []
    this.node.__r_descendants.forEach((child) =>
      elements.push(
        ...getChildrenInNArray(child.__r_elements_graph, this.node.__r_indexes)
      )
    )
    return elements
  }

  /**
   * 实体父亲
   */
  get entity(): undefined | ViewElement {
    if (this.parent?.model) return this.parent
    return this.parent?.entity
  }

  /**
   * 当前索引
   */
  get index(): number | string {
    return this.indexes.at(-1) ?? -1
  }

  /**
   * 多维索引
   */
  get indexes() {
    return this.node.__r_indexes.slice(1)
  }

  /**
   * 父节点
   */
  get parent(): undefined | ViewElement {
    const parent = this.node.__r_parent
    if (parent) {
      return getElementInNArray(parent.__r_elements_graph, this.node.__r_indexes)
    }
  }

  /**
   * 父元素集合(从自身父节点开始)
   */
  get parents(): ViewElement[] {
    if (this.parent) {
      return [this.parent, ...this.parent.parents]
    }
    return []
  }

  /**
   * 根节点
   */
  get root(): ViewElement {
    return this.parent?.root ?? this
  }

  /**
   * 状态中心
   */
  get store(): Store {
    return this.node.__r_store
  }

  constructor(node: Node) {
    super()
    this.node = node
    this.scope = new ViewScope(this)
    this.__r_initialized = true
    addElementEffect(this)
  }

  override __r_getEventParent() {
    return this.getParent() ?? this.node.__r_store
  }

  /**
   * 销毁元素
   */
  destroy(elements?: ViewElement[]) {
    this.children.forEach((element) => element.destroy())
    const node = this.node
    elements ??= getElementsInNArray(node.__r_elements_graph, node.__r_indexes)
    /**
     * 只允许销毁循环内的元素，不允许销毁循环外的元素
     */
    if (elements && Number(this.index) > -1) {
      this.dispose(true)
      elements?.splice(elements.indexOf(this), 1)
    }
  }

  /**
   * 按照文件路径字符串查找元素
   * @param address
   */
  find(address?: string): undefined | ViewElement
  /**
   * 基于过滤器查找匹配元素
   * @param filter
   */
  find<T extends ViewElement = ViewElement>(filter?: Filter<T>): T | undefined
  find(arg?: any): undefined | ViewElement {
    if (this !== this.root) return this.root.find(arg)
    if (typeof arg === 'string') {
      const node = this.node.find(arg)
      if (node !== undefined) return node.getViewElement()
      if (arg === '') return
      const graph = this.__r_address_graph
      if (arg[0] === '/') return graph[arg]
      if (this.match(arg)) return this
      for (const element of values(graph)) {
        if (element.match(arg)) return element
      }
    } else if (typeof arg === 'function') {
      if (arg(this)) return this
      for (const element of values(this.__r_address_graph)) {
        if (arg(element)) return element
      }
    }
  }

  /**
   * 基于过滤器查找匹配元素集合
   * @param filter
   * @returns
   */
  findAll<T extends ViewElement = ViewElement>(
    filter?: Filter<ViewElement>
  ): T[] {
    if (this !== this.root) return this.root.findAll(filter)
    if (typeof filter === 'function') {
      const elements = []
      if (filter(this)) elements.push(this)
      for (const element of this.descendants) {
        if (filter(element)) elements.push(element)
      }
      return elements as T[]
    } else {
      return [this, ...this.descendants] as T[]
    }
  }

  /**
   * 获取子节点集合
   */
  getChildren(): ViewElement[] {
    return this.children
  }

  /**
   * 获取子孙集合
   */
  getDescendants(): ViewElement[] {
    return this.descendants
  }

  /**
   * 获取实体父亲 ViewElement
   */
  getEntity() {
    return this.entity
  }
  /**
   * 获取父亲 ViewElement
   */

  getParent() {
    return this.parent
  }

  /**
   * 获取父节点集合(从自身父节点开始)
   */
  getParents(): ViewElement[] {
    return this.parents
  }

  /**
   * 获取根元素
   */
  getRoot(): ViewElement {
    return this.root
  }

  /**
   * 获取状态中心
   */
  getStore(): Store {
    return this.store
  }

  /**
   * 是否是根节点
   */
  isRoot() {
    return this.parent === undefined
  }

  /**
   * 路径匹配器(支持 glob 语法)
   * @param pattern
   * @returns
   */
  match(pattern: PathLikes): boolean {
    return this.address.match(pattern)
  }

  override raise(error: Error): void {
    this.node.raise(error)
  }

  toJS() {
    return {
      address: this.address.toJS(),
      index: this.index,
      indexes: this.indexes,
      model: this.model?.toJS(),
      node: this.node.toJS(),
      scope: this.scope.toJS()
    }
  }

  toJSON() {
    return {
      address: this.address.toJSON(),
      index: this.index,
      indexes: this.indexes,
      model: this.model?.toJSON(),
      node: this.node.toJSON(),
      scope: this.scope.toJSON()
    }
  }
}
